import type {
  FacilityProcessesParams,
  FacilityProcessesQueryInput,
} from '../-types';

/**
 * The three fields `facility-processes.js` maps into its `ORDER BY`.
 *
 * The route's `sort` querystring is a regex over exactly these, so any other
 * value fails schema validation and 400s the list. Unlike production processes
 * — whose old page shipped two headers outside its enum — the old facility
 * processes page offered exactly these three, so nothing is being dropped here.
 */
const SORTABLE_FIELDS = new Set(['name', 'productionProcessId', 'updatedAt']);

/** The only two columns the route will filter on. */
const FILTERABLE_FIELDS = new Set(['name', 'productionProcessId']);

/**
 * Turns table state into the endpoint's parameters.
 *
 * Single conversion point, as on relationship-types and the users roster: the
 * query key is what this returns, so the route loader (reading the URL) and the
 * page's hook (reading `useTable`) only share a cache entry if both derive
 * their params the same way.
 *
 * `page` converts from the UI's 1-based count to the endpoint's 0-based one —
 * the handler does `OFFSET page * count` with no adjustment of its own.
 * `filter` is a JSON object of `column: value`, which the route caps at 512
 * characters.
 */
export function toFacilityProcessesParams(
  input: FacilityProcessesQueryInput,
): FacilityProcessesParams {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (FILTERABLE_FIELDS.has(key) && values && values.length > 0) {
      filter[key] = values.join(',');
    }
  }
  const hasFilter = Object.keys(filter).length > 0;

  // Dropped rather than forwarded: an unsupported field would 400 the list.
  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(sort ? { sort } : {}),
    ...(hasFilter ? { filter: JSON.stringify(filter) } : {}),
  };
}

export function buildFacilityProcessesQs(
  params: FacilityProcessesParams,
): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

/**
 * Every endpoint returns `translations` parsed or null; a string is still
 * accepted defensively, so the sheet copes with an object, a string, or null
 * rather than trusting one.
 */
export function parseTranslations(
  value: unknown,
): Record<string, { name?: string }> | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'object') {
    return value as Record<string, { name?: string }>;
  }
  if (typeof value !== 'string') return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, { name?: string }>)
      : undefined;
  } catch {
    // A malformed column should leave the form blank, not crash the sheet.
    return undefined;
  }
}
